import { plural, t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { useValidDurations } from "@/features/BookingWizard/hooks/useValidDurations/useValidDurations";
import { isAvailableForBookingDuration } from "@/utils/slotAvailability";
import { DropdownSelectOption } from "@whoosh/design-system/src/components/controls";

import { useGetValidCourseAndDurationSelections_Node$key } from "./__generated__/useGetValidCourseAndDurationSelections_Node.graphql";

export const useGetValidCourseAndDurationSelections = (
  fragmentRef: useGetValidCourseAndDurationSelections_Node$key | null,
  groupSize: number
) => {
  const data = useFragment(
    useGetValidSelectionsForCurrentCourseFragmentString,
    fragmentRef
  );
  const validDurationsForCurrentSlot = useValidDurations(data ?? null);

  const customerCanSelectCourt =
    data?.course?.facility?.publicBookingPermissionSet?.customerCanSelectCourt;

  const validDurationsFromCourseBucket = useMemo(() => {
    return customerCanSelectCourt
      ? data?.course?.courseBucket?.slotDurations.map((selection) => {
          const endSlotOptions =
            selection?.startSlot?.endSlotOptions?.edges
              ?.filter((edge) =>
                isAvailableForBookingDuration(edge?.node?.availability)
              )
              .map((edge) => {
                return {
                  durationMins: edge?.durationMins ?? 0,
                  allowedPlayerCounts:
                    edge?.allowedPlayerCounts?.flatMap((a) => (a ? a : [])) ??
                    [],
                  slotId: edge?.node?.id ?? "",
                };
              }) ?? [];
          return {
            courseId: selection?.startSlot?.course?.id ?? "",
            endSlotOptions,
          };
        }) ?? []
      : [];
  }, [customerCanSelectCourt, data?.course?.courseBucket?.slotDurations]);

  const allValidDurationsOptionsMappedByCourseId = useMemo(() => {
    const currentSlotOptions = {
      courseId: data?.course?.id ?? "",
      endSlotOptions: validDurationsForCurrentSlot ?? [],
    };
    return [currentSlotOptions, ...validDurationsFromCourseBucket];
  }, [
    data?.course?.id,
    validDurationsForCurrentSlot,
    validDurationsFromCourseBucket,
  ]);

  const validDurationOptions: DropdownSelectOption<number>[] = useMemo(() => {
    // Get all duration options across all courses
    const allDurations = allValidDurationsOptionsMappedByCourseId.flatMap(
      (courseOption) =>
        courseOption.endSlotOptions.map((option) => ({
          durationMins: option.durationMins,
          allowedPlayerCounts: option.allowedPlayerCounts,
        }))
    );

    // Group by duration value and combine allowed player counts
    const durationMap = allDurations.reduce((acc, curr) => {
      const existing = acc.get(curr.durationMins);
      if (existing) {
        // Combine and dedupe allowed player counts
        const combinedCounts = Array.from(
          new Set([
            ...existing.allowedPlayerCounts,
            ...curr.allowedPlayerCounts,
          ])
        );
        acc.set(curr.durationMins, {
          ...curr,
          allowedPlayerCounts: combinedCounts,
        });
      } else {
        acc.set(curr.durationMins, curr);
      }
      return acc;
    }, new Map<number, { durationMins: number; allowedPlayerCounts: number[] }>());

    // Convert map to array and sort by duration
    return Array.from(durationMap.values())
      .sort((a, b) => a.durationMins - b.durationMins)
      .map((duration) => ({
        label: durationDisplayFromMinutes(duration.durationMins) || "",
        value: duration.durationMins,
        isDisabled: groupSize
          ? duration.allowedPlayerCounts.includes(groupSize)
            ? false
            : true
          : false,
        subLabel: getDurationSubLabel(
          duration.allowedPlayerCounts,
          groupSize ?? 0
        ),
      }));
  }, [allValidDurationsOptionsMappedByCourseId, groupSize]);

  return { validDurationOptions, allValidDurationsOptionsMappedByCourseId };
};

export const getDurationSubLabel = (
  allowedPlayerCounts: number[],
  currentGroupSize: number
): string | undefined => {
  // If current group size is allowed, return null
  if (allowedPlayerCounts.includes(currentGroupSize)) {
    return undefined;
  }

  // Find nearest lower and higher numbers
  const lowerNumbers = allowedPlayerCounts.filter((n) => n < currentGroupSize);
  const higherNumbers = allowedPlayerCounts.filter((n) => n > currentGroupSize);

  const nearestLower = lowerNumbers.length
    ? Math.max(...lowerNumbers)
    : undefined;
  const nearestHigher = higherNumbers.length
    ? Math.min(...higherNumbers)
    : undefined;

  // Build return string based on available options
  if (nearestLower && nearestHigher) {
    return t`Requires ${nearestLower} or ${nearestHigher} Players`;
  } else if (nearestLower) {
    return plural(nearestLower, {
      one: "Requires # Players",
      other: "Requires # Players",
    });
  } else if (nearestHigher) {
    return plural(nearestHigher, {
      one: "Requires # Players",
      other: "Requires # Players",
    });
  }

  return undefined;
};

export const durationDisplayFromMinutes = (
  totalMinutes: number | null
): string | null => {
  if (totalMinutes === null) {
    return null;
  }
  if (totalMinutes === 0) return t`0 Minute`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hourLabel =
    hours >= 1 ? plural(hours, { one: "# Hour", other: "# Hours" }) : "";
  const minuteLabel =
    minutes || !hours
      ? plural(minutes, { one: "# Minute", other: "# Minutes" })
      : "";
  return [hourLabel, minuteLabel].filter(Boolean).join(" ");
};

export const useGetValidSelectionsForCurrentCourseFragmentString = graphql`
  fragment useGetValidCourseAndDurationSelections_Node on Node {
    ...useValidDurationsNode
    ... on TimeSlot {
      id
      agenda {
        id
      }
      time
      course {
        id
        name
        facility {
          publicBookingPermissionSet {
            customerCanSelectCourt
          }
        }
        courseBucket {
          id
          slotDurations(agendaId: $agendaId, startsAt: $startTime) {
            startSlot {
              id
              course {
                id
                name
              }
              endSlotOptions(
                bookingId: $bookingId
                bookingType: $bookingType
                memberId: $memberId
              ) {
                edges {
                  durationMins
                  allowedPlayerCounts
                  node {
                    id
                    time
                    availability
                  }
                }
              }
            }

            maxDurationMins
          }
        }
      }
    }
  }
`;
